import AppConfig from "@/core/config/AppConfig";
import store from "@/store";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import axios, { AxiosInstance } from "axios";
import NotificationDialog from "@/core/helpers/notificationDialog";
import { getWord } from "@/core/helpers/translator";
import JwtService from "../JwtService";
import { downloadFromBlob } from "@/core/helpers/fileDownloadHelper";

class BaseApiService {
    private headers: any = null;
    private static isGetDashboardDataProcessing = false

    constructor(token: string | null | undefined = "", withFile = false) {
        if (withFile) {
            this.headers = {
                'Content-Type': 'multipart/form-data',
                "AppKey": AppConfig.Api.AppKey,
                "Access-Control-Allow-Origin": "*"
            };
        }
        else {
            this.headers = {
                "Content-Type": "application/json",
                "AppKey": AppConfig.Api.AppKey,
                "Access-Control-Allow-Origin": "*"
            };
        }

        const timeZone = JwtService.getTimeZone();
        if (timeZone) {
            this.headers.TimeZone = timeZone;
        }

        if (token) {
            this.headers.AuthToken = `ArenaRobotics ${token}`;
        }
    }

    public Build(baseUrl: string) {
        const apiClient: AxiosInstance = axios.create({
            baseURL: baseUrl,
            headers: this.headers,
            timeout: 0
        });

        return apiClient;
    }

    public BuildForDownload(baseUrl: string) {
        const apiClient: AxiosInstance = axios.create({
            baseURL: baseUrl,
            headers: this.headers,
            timeout: 0,
            responseType: "blob"
        });

        return apiClient;
    }

    public static GetDashboardData() {
        const token = JwtService.getToken()
        const client = new BaseApiService(token)
            .Build(AppConfig.Api.Orchestrator.BaseUrl);

        if (!BaseApiService.isGetDashboardDataProcessing) {
            BaseApiService.isGetDashboardDataProcessing = true;
            client.get("/dashboard").then((response) => {
                if (response.data) {
                    if (response.data.agent_dashboard && response.data.agent_dashboard != "Unauthorized Access" && response.data.orch_dashboard && response.data.orch_dashboard != "Unauthorized Access") {
                        store.dispatch(Actions.SET_DASHBOARD_DATA, response.data)
                    }
                    else {
                        store.commit(Mutations.SET_DASHBOARD_DATA_FETCHED, false)
                    }
                }
                else {
                    store.commit(Mutations.SET_DASHBOARD_DATA_FETCHED, false)
                }
            }).catch(() => {
                store.commit(Mutations.SET_DASHBOARD_DATA_FETCHED, false)
            }).finally(() => {
                BaseApiService.isGetDashboardDataProcessing = false;
            })
        }
    }

    public static Search(searchTerm) {
        return new Promise((resolve) => {
            const token = JwtService.getToken();
            const orchClient = new BaseApiService(token)
                .Build(AppConfig.Api.Orchestrator.BaseUrl);

            const customerClient = new BaseApiService(token)
                .Build(AppConfig.Api.Customer.BaseUrl);

            orchClient.get("/globalSearch/" + searchTerm).then((response) => {
                if (response.data) {
                    if (response.data.orch_globalSearch && response.data.orch_globalSearch != "Unauthorized Access") {
                        store.dispatch(Actions.SET_SEARCH_RESULT_ORCH, response.data.orch_globalSearch.data)
                    }
                    else {
                        store.dispatch(Actions.SET_SEARCH_RESULT_ORCH, { robots: null });
                    }

                    if (response.data.identity_globalSearch && response.data.identity_globalSearch != "Unauthorized Access") {
                        store.dispatch(Actions.SET_SEARCH_RESULT_IDENTITY, response.data.identity_globalSearch.data)
                    }
                    else {
                        store.dispatch(Actions.SET_SEARCH_RESULT_ORCH, { users: null });
                    }

                    if (response.data.agent_globalSearch && response.data.agent_globalSearch != "Unauthorized Access") {
                        store.dispatch(Actions.SET_SEARCH_RESULT_AGENT, response.data.agent_globalSearch.data)
                    }
                    else {
                        store.dispatch(Actions.SET_SEARCH_RESULT_ORCH, { assets: null, credentials: null, packages: null, processes: null, queues: null, triggers: null });
                    }
                }

                customerClient.get("/globalSearch/" + searchTerm).then((response) => {
                    if (response.data) {
                        store.dispatch(Actions.SET_SEARCH_RESULT_CUSTOMER, response.data);
                    }
                })
                    .catch(() => {
                        store.dispatch(Actions.SET_SEARCH_RESULT_CUSTOMER, { data: { machines: null } });
                    })
                    .finally(() => {
                        resolve(true);
                    })
            }).catch(() => {
                resolve(false);
            });
        });
    }

    public static ClearCache() {
        const token = JwtService.getToken()
        const client = new BaseApiService(token).Build(AppConfig.Api.Orchestrator.BaseUrl);

        client.get("/cache/clear")
            .then(({ data }) => {
                if (data.agent_clear_cache === true && data.identity_clear_cache === true && data.orch_clear_cache === true) {
                    /* data.agent_clear_cache may equal to "Forbidden" so "if(data.agent_clear_cache)" check will return true. that's why we used like "if(data.agent_clear_cache === true)" */

                    const customerClient = new BaseApiService(token).Build(AppConfig.Api.Customer.BaseUrl);
                    customerClient.post("/cache/clear")
                        .then((_response) => {
                            if (_response.data) {
                                NotificationDialog.Notify(getWord("CacheCleared"));
                            }
                        })
                }
            });
    }

    public static DeleteFaultedJobs() {
        const token = JwtService.getToken()
        const client = new BaseApiService(token).Build(AppConfig.Api.Agent.BaseUrl);

        client.post("/job/clearFaulted")
            .then(({ data }) => {
                if (!data) {
                    NotificationDialog.NotifyError(getWord("FaultedJobDeleteFailed"));
                }
                else {
                    NotificationDialog.Notify(getWord("FaultedJobsDeleted"));
                }
            });
    }

    public static GetListForSelectbox(apiClient: AxiosInstance, url: string, params: any = {}) {
        return apiClient.get(url, params)
    }

    public static GetList(apiClient: AxiosInstance, url: string, params: any = {}) {
        store.commit(Mutations.SET_FETCH_DATA, false);
        store.commit(Mutations.SET_TABLE_LOADING, true);
        return apiClient.get(url, params).catch((error) => {
            const err = new Error(error)
            if (!BaseApiService.HandleTokenExpiration(error)) {
                store.dispatch(Actions.HANDLE_COLLECTION_RESULT, err)
            }
            throw err;
        }).finally(() => {
            store.commit(Mutations.SET_TABLE_LOADING, false)
            store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading")
        })
    }

    public static ExportList(apiClient: AxiosInstance, url: string, params: any = {}) {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading")

        return apiClient.get(url, params)
            .then((response) => {
                const fileName = `${store.getters.getDataType}Report_${Date.now().toLocaleString().replaceAll(",", "")}`;
                downloadFromBlob(response.data, fileName, "xlsx");
                NotificationDialog.Success(fileName + ".xlsx " + getWord("FileDownloadCompleted"));
            })
            .catch((error) => {
                const err = new Error(error)
                if (!BaseApiService.HandleTokenExpiration(error)) {
                    NotificationDialog.Error();
                }
                throw err;
            })
            .finally(() => {
                store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
                store.commit(Mutations.SET_EXPORT_DATA, false);
            })
    }

    public static GetById(apiClient: AxiosInstance, url: string, params: any = {}) {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading")
        return apiClient.get(url, params).catch((error) => {
            const err = new Error(error)
            BaseApiService.HandleError(error)
            throw err;
        }).finally(() => {
            store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading")
        })
    }

    public static Create(apiClient: AxiosInstance, url: string, model: any = {}) {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading")
        return apiClient.post(url, model).catch((error) => {
            const err = new Error(error)
            BaseApiService.HandleError(error)
            throw err;
        }).finally(() => {
            store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading")
        })
    }

    public static Update(apiClient: AxiosInstance, url: string, model: any = {}, showLoading = true) {
        if (showLoading)
            store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading")
        return apiClient.put(url, model).catch((error) => {
            const err = new Error(error)
            BaseApiService.HandleError(error)
            throw err;
        }).finally(() => {
            if (showLoading)
                store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading")
        })
    }

    public static Delete(apiClient: AxiosInstance, url: string) {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading")
        return apiClient.delete(url).catch((error) => {
            const err = new Error(error)
            BaseApiService.HandleError(error)
            throw err;
        }).finally(() => {
            store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading")
        })
    }

    public static HandleError(err: any) {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");

        if (this.HandleTokenExpiration(err)) {
            return;
        }

        if (err?.response?.data?.messages?.length > 0) {
            const descriptions: string[] = err.response.data.messages.map(
                (message: any) => getWord(message.description)
            );

            NotificationDialog.Error(descriptions.join(", <br/>"));
        } else {
            NotificationDialog.Error(getWord("SomethingWentWrong"));
        }
    }

    private static HandleTokenExpiration(err: any) {
        const tokenDestroyed = err?.response?.data?.includes && err.response.data.includes("TokenDestroyed");
        const tokenExpired = err?.response?.data === "TokenExpired";
        if (tokenDestroyed || tokenExpired) {
            store.commit(Mutations.PURGE_AUTH, true);
            NotificationDialog.NotifyInfo(tokenDestroyed ? getWord("YourSessionHasEnded") : getWord("YourSessionHasExpired"));
            return true;
        }

        return false;
    }
}

export default BaseApiService;